import { useMemo, useState } from "react";
import {
  requiredFlareHeight,
  lookupFFactor,
  type FlareHeightInput,
} from "../calc/flareRadiation";
import {
  ALLOWABLE_RADIATION_LEVELS,
  F_FACTOR_GAS_LABELS,
  type FFactorGas,
} from "../data/flareRadiation";
import { Field, Item, num } from "./formBits";
import { FlareGeometryDiagram } from "./FlareGeometryDiagram";

interface FormState {
  massFlowKgH: string;
  molecularWeight: string;
  temperatureK: string;
  compressibility: string;
  tipPressureKpaAbs: string;
  tipInsideDiameterM: string;
  windVelocityMs: string;
  heatReleaseMode: "direct" | "fromMassFlow";
  heatReleaseKw: string;
  lowerHeatingValueKjKg: string;
  fFactorMode: "table" | "direct";
  fFactorGas: FFactorGas;
  burnerDiameterCm: string;
  fractionHeatRadiated: string;
  transmissivity: string;
  allowableRadiationKwM2: string;
  gradeDistanceM: string;
}

/** 초기값은 API 521(7판) Annex C.2.2 예제(Ma=0.2 케이스) 조건이다. */
const INITIAL: FormState = {
  massFlowKgH: "45360",
  molecularWeight: "46.1",
  temperatureK: "422",
  compressibility: "1.0",
  tipPressureKpaAbs: "101.3",
  tipInsideDiameterM: "0.468",
  windVelocityMs: "8.94",
  heatReleaseMode: "direct",
  heatReleaseKw: "630000",
  lowerHeatingValueKjKg: "50000",
  fFactorMode: "direct",
  fFactorGas: "methaneNaturalGas",
  burnerDiameterCm: "46.8",
  fractionHeatRadiated: "0.3",
  transmissivity: "1.0",
  allowableRadiationKwM2: "6.3",
  gradeDistanceM: "45.7",
};

function fmt(v: number, digits = 2): string {
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString("ko-KR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function FlareRadiationPanel() {
  const [f, setF] = useState<FormState>(INITIAL);

  const set =
    <K extends keyof FormState>(key: K) =>
    (value: FormState[K]) =>
      setF((prev) => ({ ...prev, [key]: value }));

  const fFactorLookup = useMemo(
    () =>
      f.fFactorMode === "table"
        ? lookupFFactor(f.fFactorGas, num(f.burnerDiameterCm))
        : null,
    [f.fFactorMode, f.fFactorGas, f.burnerDiameterCm],
  );

  const result = useMemo(() => {
    const input: FlareHeightInput = {
      massFlowKgH: num(f.massFlowKgH),
      molecularWeight: num(f.molecularWeight),
      temperatureK: num(f.temperatureK),
      compressibility: num(f.compressibility),
      tipPressureKpaAbs: num(f.tipPressureKpaAbs),
      tipInsideDiameterM: num(f.tipInsideDiameterM),
      windVelocityMs: num(f.windVelocityMs),
      heatReleaseMode: f.heatReleaseMode,
      heatReleaseKw: num(f.heatReleaseKw),
      lowerHeatingValueKjKg: num(f.lowerHeatingValueKjKg),
      fractionHeatRadiated:
        f.fFactorMode === "table" && fFactorLookup !== null
          ? fFactorLookup.fFactor
          : num(f.fractionHeatRadiated),
      transmissivity: num(f.transmissivity),
      allowableRadiationKwM2: num(f.allowableRadiationKwM2),
      gradeDistanceM: num(f.gradeDistanceM),
    };
    return requiredFlareHeight(input);
  }, [f, fFactorLookup]);

  return (
    <section className="card">
      <h2>플레어 복사열·높이 (API 521 Annex F.2 단순법)</h2>
      <p className="hint">
        API 521(7판) 5.5.5·Annex F.2 "단순법" — 화염을 점원(point source)
        하나로 보는 예비 스크리닝용 계산입니다. 화염길이(Figure F.1/F.2)·
        바람에 의한 화염 기울어짐(Figure F.3)은 표준 그래프를 디지털화한
        값을 씁니다 — Annex C.2.2 예제(h=33.3 m / 25.9 m)로 전체 절차를
        대조해 두었지만, 정밀 설계에는 플레어 제작사 확인이 필요합니다.
        초기값은 그 예제(Ma=0.2 케이스) 조건입니다.
      </p>

      <h3>가스 조건</h3>
      <div className="grid-form">
        <Field label="질량유량" unit="kg/h" help="flrMassFlow">
          <input value={f.massFlowKgH} onChange={(e) => set("massFlowKgH")(e.target.value)} />
        </Field>
        <Field label="분자량" unit="kg/kmol" help="flrMolecularWeight">
          <input
            value={f.molecularWeight}
            onChange={(e) => set("molecularWeight")(e.target.value)}
          />
        </Field>
        <Field label="팁 온도" unit="K" help="flrTemperature">
          <input value={f.temperatureK} onChange={(e) => set("temperatureK")(e.target.value)} />
        </Field>
        <Field label="압축계수 Z" unit="" help="flrCompressibility">
          <input
            value={f.compressibility}
            onChange={(e) => set("compressibility")(e.target.value)}
          />
        </Field>
        <Field label="팁 압력" unit="kPa abs" help="flrTipPressure">
          <input
            value={f.tipPressureKpaAbs}
            onChange={(e) => set("tipPressureKpaAbs")(e.target.value)}
          />
        </Field>
        <Field label="팁 내경" unit="m" help="flrTipDiameter">
          <input
            value={f.tipInsideDiameterM}
            onChange={(e) => set("tipInsideDiameterM")(e.target.value)}
          />
        </Field>
        <Field label="설계 풍속" unit="m/s" help="flrWindVelocity">
          <input
            value={f.windVelocityMs}
            onChange={(e) => set("windVelocityMs")(e.target.value)}
          />
        </Field>
      </div>

      <h3>발열량</h3>
      <div className="grid-form">
        <Field label="입력 방식" unit="" help="flrHeatReleaseMode">
          <select
            value={f.heatReleaseMode}
            onChange={(e) => set("heatReleaseMode")(e.target.value as "direct" | "fromMassFlow")}
          >
            <option value="direct">직접 입력</option>
            <option value="fromMassFlow">질량유량×LHV 자동계산</option>
          </select>
        </Field>
        {f.heatReleaseMode === "direct" ? (
          <Field label="발열량 Q" unit="kW" help="flrHeatReleaseDirect">
            <input
              value={f.heatReleaseKw}
              onChange={(e) => set("heatReleaseKw")(e.target.value)}
            />
          </Field>
        ) : (
          <Field label="저위발열량 LHV" unit="kJ/kg" help="flrLhv">
            <input
              value={f.lowerHeatingValueKjKg}
              onChange={(e) => set("lowerHeatingValueKjKg")(e.target.value)}
            />
          </Field>
        )}
      </div>

      <h3>복사열 분율 F (Table F.1)</h3>
      <div className="grid-form">
        <Field label="입력 방식" unit="" help="flrFFactorMode">
          <select
            value={f.fFactorMode}
            onChange={(e) => set("fFactorMode")(e.target.value as "table" | "direct")}
          >
            <option value="direct">직접 입력</option>
            <option value="table">표에서 조회</option>
          </select>
        </Field>
        {f.fFactorMode === "table" ? (
          <>
            <Field label="가스 종류" unit="" help="flrFFactorGas">
              <select
                value={f.fFactorGas}
                onChange={(e) => set("fFactorGas")(e.target.value as FFactorGas)}
              >
                {(Object.keys(F_FACTOR_GAS_LABELS) as FFactorGas[]).map((g) => (
                  <option key={g} value={g}>
                    {F_FACTOR_GAS_LABELS[g]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="버너(팁) 직경" unit="cm" help="flrBurnerDiameter">
              <input
                value={f.burnerDiameterCm}
                onChange={(e) => set("burnerDiameterCm")(e.target.value)}
              />
            </Field>
          </>
        ) : (
          <Field label="F (복사열 분율)" unit="0~1" help="flrFFactorDirect">
            <input
              value={f.fractionHeatRadiated}
              onChange={(e) => set("fractionHeatRadiated")(e.target.value)}
            />
          </Field>
        )}
      </div>
      {f.fFactorMode === "table" && fFactorLookup && (
        <p className="hint">
          조회된 F = {fmt(fFactorLookup.fFactor, 3)}
          {fFactorLookup.clamped && " (표 범위를 벗어나 가장 가까운 끝값으로 고정됨)"}
        </p>
      )}

      <h3>허용 방사강도·검토 지점</h3>
      <div className="grid-form">
        <Field label="대기 투과율 τ" unit="0~1" help="flrTransmissivity">
          <input
            value={f.transmissivity}
            onChange={(e) => set("transmissivity")(e.target.value)}
          />
        </Field>
        <Field label="허용 방사강도 K" unit="kW/m²" help="flrAllowableRadiation">
          <input
            value={f.allowableRadiationKwM2}
            onChange={(e) => set("allowableRadiationKwM2")(e.target.value)}
          />
        </Field>
        <Field label="수평거리 r" unit="m" help="flrGradeDistance">
          <input
            value={f.gradeDistanceM}
            onChange={(e) => set("gradeDistanceM")(e.target.value)}
          />
        </Field>
      </div>
      <div className="scenario__check" style={{ flexWrap: "wrap", gap: 8 }}>
        {ALLOWABLE_RADIATION_LEVELS.map((lv) => (
          <button
            key={lv.kwM2}
            type="button"
            className="btn btn--ghost"
            title={lv.conditionKo}
            onClick={() => set("allowableRadiationKwM2")(String(lv.kwM2))}
          >
            Table 12: {lv.kwM2} kW/m²
          </button>
        ))}
      </div>

      {!result.ok ? (
        <div className="result result--error" role="alert">
          {result.message}
        </div>
      ) : (
        <div className="result result--ok">
          <div className="result__headline">
            <span className="result__label">필요 플레어 높이</span>
            <strong className="result__value">{fmt(result.requiredHeightM, 1)}</strong>
            <span className="result__label">m</span>
          </div>

          <h3>중간값 (계산서 기재용)</h3>
          <dl className="result__grid">
            <Item label="발열량 Q" value={`${fmt(result.heatReleaseKw, 0)} kW`} />
            <Item label="화염길이 L" value={`${fmt(result.flameLengthM, 1)} m`} />
            <Item
              label="팁 유출속도 uⱼ"
              value={`${fmt(result.tipVelocityMs, 1)} m/s`}
            />
            <Item
              label="풍속/유출속도 비 u∞/uⱼ"
              value={fmt(result.windToTipVelocityRatio, 4)}
            />
            <Item
              label="수평 변위 Δx"
              value={`${fmt(result.horizontalDisplacementM, 1)} m`}
            />
            <Item
              label="수직 변위 Δy"
              value={`${fmt(result.verticalDisplacementM, 1)} m`}
            />
            <Item
              label="복사거리 D (Eq F.1)"
              value={`${fmt(result.radiationDistanceM, 1)} m`}
            />
          </dl>

          {result.warnings.length > 0 && (
            <ul className="warnings">
              {result.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}

          <h3>개략도</h3>
          <FlareGeometryDiagram
            heightM={result.requiredHeightM}
            horizontalDisplacementM={result.horizontalDisplacementM}
            verticalDisplacementM={result.verticalDisplacementM}
            radiationDistanceM={result.radiationDistanceM}
            gradeDistanceM={num(f.gradeDistanceM)}
            windVelocityMs={num(f.windVelocityMs)}
            allowableRadiationKwM2={num(f.allowableRadiationKwM2)}
          />
        </div>
      )}
    </section>
  );
}
